import React from "react";

import { Modal } from "antd";
import { Form, Formik, Field, FieldProps, FormikHandlers } from "formik";
import * as Yup from "yup";
import { encrypt } from "../../utils/crypto";

import * as S from "./style";
import { InputWrapper } from "../InputWrapper";
import DSButton from "../DSButton";
import { DSInput } from "../DSInput";

type ChangeCardPasswordSchema = {
  password: string;
  confirmPassword: string;
};

type ChangeCardPasswordModalProps = {
  onClose: () => void;
  isOpen: boolean;
  onSubmitChangeCardPassword: (
    encryptedPassword: string
  ) => Promise<void> | undefined;
  isSubmitting?: boolean;
  cardPasswordLength?: number;
};

export function ChangeCardPasswordModal({
  onClose,
  isOpen,
  onSubmitChangeCardPassword,
  isSubmitting = false,
  cardPasswordLength,
}: ChangeCardPasswordModalProps) {
  const changeCardPasswordSchema = Yup.object({
    password: Yup.string().required("A senha é obrigatória"),
    confirmPassword: Yup.string().required(
      "A confirmação de senha é obrigatória"
    ),
  })
    .test("arePasswordsEqual", (values, ctx) => {
      if (values.password !== values.confirmPassword) {
        return ctx.createError({
          path: "confirmPassword",
          message: "As senhas não conferem",
        });
      }
      return true;
    })
    .test("isLengthEqual", (values, ctx) => {
      const password = values.password ?? "";
      if (password.length > 0 && password.length !== cardPasswordLength) {
        return ctx.createError({
          path: "password",
          message: `A nova senha deve conter ${cardPasswordLength}
            dígitos numéricos`,
        });
      }
      return true;
    });
  const onSubmit = (data: ChangeCardPasswordSchema) => {
    const passCrypt = encrypt(data.password);

    onSubmitChangeCardPassword(passCrypt);
  };

  const onChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChangeFn: FormikHandlers["handleChange"]
  ) => {
    const newValue = e.target.value.replace(/\D/g, "");
    const newEvent = {
      ...e,
    };
    newEvent.target.value = newValue;
    onChangeFn(newEvent);
  };

  return (
    <Modal
      title="Alterar Senha"
      open={isOpen}
      onCancel={onClose}
      destroyOnClose
      closable={!isSubmitting}
      maskClosable={!isSubmitting}
    >
      <S.FormWrapper>
        <p>
          {`
            Deseja realmente alterar a senha do cartão? A nova senha deve conter ${cardPasswordLength}
            dígitos numéricos
          `}
        </p>
        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          validationSchema={changeCardPasswordSchema}
          onSubmit={onSubmit}
        >
          <Form>
            <Field name="password">
              {({ field, meta }: FieldProps<string>) => (
                <InputWrapper
                  label="Nova senha"
                  error={meta.touched && meta.error ? meta.error : undefined}
                  isRequired
                  name={field.name}
                >
                  <DSInput
                    type="password"
                    placeholder="Digite a nova senha"
                    {...field}
                    touched={meta.touched}
                    error={!!meta.error}
                    onChange={(e) => onChangeInput(e, field.onChange)}
                    maxLength={cardPasswordLength}
                    disabled={isSubmitting}
                    data-testid="input_nova_senha_cartao"
                  />
                </InputWrapper>
              )}
            </Field>

            <Field name="confirmPassword">
              {({ field, meta }: FieldProps<string>) => (
                <InputWrapper
                  label="Confirme a nova senha"
                  error={meta.touched && meta.error ? meta.error : undefined}
                  isRequired
                  name={field.name}
                >
                  <DSInput
                    type="password"
                    placeholder="Digite a confirmação da nova senha"
                    {...field}
                    touched={meta.touched}
                    error={!!meta.error}
                    onChange={(e) => onChangeInput(e, field.onChange)}
                    maxLength={cardPasswordLength}
                    disabled={isSubmitting}
                    data-testid="input_confirme_nova_senha_cartao"
                  />
                </InputWrapper>
              )}
            </Field>
            <S.ButtonsWrapper>
              <DSButton
                type="reset"
                buttonText="Cancelar"
                buttonType="secondary"
                isCancelType
                onClick={onClose}
                height={30}
                style={{
                  padding: "0.4rem 1.5rem",
                }}
                isDisabled={isSubmitting}
                disabled={isSubmitting}
                dataTestId="botao_cancelar_nova_senha_modal"
              />
              <DSButton
                type="submit"
                buttonText="Confirmar"
                buttonType="secondary"
                height={30}
                style={{
                  padding: "0.4rem 1.5rem",
                }}
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                disabled={isSubmitting}
                dataTestId="botao_confirmar_nova_senha_modal"
              />
            </S.ButtonsWrapper>
          </Form>
        </Formik>
      </S.FormWrapper>
    </Modal>
  );
}
